import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BaseComponent } from '@core/components/base-component';
import { CostTypeHelper } from '@core/helpers/cost-type.helper';
import { NumberHelper } from '@core/helpers/number.helper';
import { SortHelper } from '@core/helpers/sort.helper';
import { TreeNodeHelper } from '@core/helpers/tree-node.helper';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { EstimateTypeEnum } from '@enums/EstimateType';
import { StatusEnum } from '@enums/Status';
import { StructureDepthEnum } from '@enums/StructureDepth';
import { AverageRateDto } from '@interfaces/AverageRateDto';
import { ClassificationEstimate } from '@interfaces/ClassificationEstimate';
import { CompanyCurrency } from '@interfaces/CompanyCurrency';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { EstimateLine } from '@interfaces/EstimateLine';
import { ExpenseBillingMethodDto } from '@interfaces/ExpenseBillingMethodDto';
import { ExpenseLine } from '@interfaces/ExpenseLine';
import { GroupCostTypeDto } from '@interfaces/GroupCostTypeDto';
import { InternalFeeBillingMethodDto } from '@interfaces/InternalFeeBillingMethodDto';
import { LaborBillingMethodDto } from '@interfaces/LaborBillingMethodDto';
import { Level1Detail } from '@interfaces/Level1Detail';
import { Structure } from '@interfaces/Structure';
import { TreeNodeDetail } from '@interfaces/common/tree-node-detail';
import { TreeNodeTotals } from '@models/TreeNodeTotals';
import { SECTION_FILTER_ITEMS, SectionFilterItem } from '@models/consts/estimate-section-filter-items.const';
import { TranslateService } from '@ngx-translate/core';
import { AppSettingsService } from '@shared/services/app-settings.service';
import { CurrencySymbolService } from '@shared/services/currency-symbol.service';
import { CurrencyService } from '@shared/services/currency.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { ExpenseService } from '@shared/services/expense.service';
import { ProjectTotals } from '@shared/services/models/project-totals.model';
import { StructureService } from '@shared/services/structure.service';
import { TotalsDataService } from '@shared/services/total.service';
import { UserService } from '@shared/services/user.service';
import { Entity } from 'breeze-client';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { TreeTableEditEvent } from 'primeng/treetable';
import { EMPTY, Observable, combineLatest, forkJoin, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { InternalResource_Estimate } from '../../models/interfaces/InternalResource_Estimate';
import { TreeNodeData, TreeNodeType } from './models/tree-node-data.model';
import { TreeNodeContainer } from './models/tree-node.model';

export enum TreeTableColumnWidths {
  default = '4em',
  medium = '3em',
  small = '2.5em',
  icon = '1em'
}

@Component({
  selector: 'pop-treetable',
  templateUrl: './treetable.component.html',
  styleUrls: ['./treetable.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class TreetableComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly projectType = TreeNodeType.Project;
  private readonly level1Type = TreeNodeType.Level1;
  public readonly level2Type = TreeNodeType.Level2;
  public readonly departmentType = TreeNodeType.Department;
  public readonly categoryType = TreeNodeType.Category;
  private readonly genericTimeType = TreeNodeType.GenericTime;
  private readonly genericExpenseType = TreeNodeType.GenericExpense;
  private readonly genericInternalFeeType = TreeNodeType.GenericInternalFee;

  public hasPC = false;
  private averageRates: AverageRateDto[] = [];
  private companyCurrencies: CompanyCurrency[] = [];
  private expenseBillingMethods: ExpenseBillingMethodDto[] = [];
  private internalFeeBillingMethods: InternalFeeBillingMethodDto[] = [];
  private laborBillingMethods: LaborBillingMethodDto[] = [];
  private projectTotals: ProjectTotals;
  public activateInternalFees: boolean;
  public areDatesHidden = true;
  public currencySymbol: string;
  public currentLang: string;
  public dateColspan = 2;
  public estimateConfig: EstimateConfiguration;
  public estimateTree: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>[] = [];
  public functionalCurrency: string;
  public functionalCurrencySymbol: string;
  public groupCostTypes: GroupCostTypeDto[] = [];
  public isBusy: Record<string, boolean> = {};
  public isCollapsed = true;
  public isEstimateOnFunctionalCurrency = true;
  public isExpanded = false;
  public isLoaded = false;
  public isPercentHidden: Record<TreeNodeType, boolean>;
  public isTableEditable = false;
  public lowestExpenseLevel: TreeNodeType;
  public lowestInternalFeeLevel: TreeNodeType;
  public lowestTimeLevel: TreeNodeType;
  public scrollBodyHeight = '660px';
  public sectionFilters: Partial<Record<keyof EstimateConfiguration, boolean>> = {};
  public selectedNote: string = null;
  public selectedRowData: TreeNodeData = <TreeNodeData>{};
  public totalRecords: number;

  // Classification
  public isClassColumnsCollapsed = true;
  public feesColumns: TreeTableColumnWidths[] = [];
  public classificationEstimates: ClassificationEstimate[] = [];

  // Internal Fees
  public isInternalResourceColumnsCollapsed = true;
  public internalFeesColumns: TreeTableColumnWidths[] = [];
  public internalResourceEstimates: InternalResource_Estimate[] = [];

  // Expenses
  public expenseColumns: TreeTableColumnWidths[] = [];
  public isExpenseColumnCollapsed = true;

  // Totals
  public totalColumns: TreeTableColumnWidths[] = [];

  private _currentEstimate: Estimate = {} as Estimate;
  public get currentEstimate(): Estimate {
    return this._currentEstimate;
  }
  public set currentEstimate(v: Estimate) {
    this._currentEstimate = v;
    this.updateCurrentEstimate();
  }

  @Input()
  public isGroupApproved: boolean;
  @Input()
  public set sectionFilterItems(values: SectionFilterItem[]) {
    // Get hiddenSections, if a section is not present in manual filter values, it means it is hidden by the global config (default true)
    this.sectionFilters = SECTION_FILTER_ITEMS.reduce(
      (acc, curr) => ({ ...acc, [curr.key]: values.find(v => v.key === curr.key)?.selected ?? true }),
      {}
    );
    this.setColumns();
  }

  @ViewChild('treeTableParent', {
    static: false
  })
  public set treeTableParent(value: ElementRef<HTMLDivElement>) {
    if (!value) {
      return;
    }
    setTimeout(() => {
      // TODO remove this workaround once this issue is re-opened and then closed
      // https://github.com/primefaces/primeng/issues/9545

      const headerHeight = 100;
      const bodyHeight = value.nativeElement.getBoundingClientRect().height - headerHeight;

      this.scrollBodyHeight = `${bodyHeight}px`;
    });
  }

  public constructor(
    private estimateService: EstimateService,
    private totalsData: TotalsDataService,
    private currencySymbolService: CurrencySymbolService,
    private confirmationService: ConfirmationService,
    private currencyService: CurrencyService,
    private translate: TranslateService,
    private structureService: StructureService,
    private expenseService: ExpenseService,
    private userService: UserService,
    private estimateSettingsService: EstimateSettingsService,
    private appSettingsService: AppSettingsService
  ) {
    super();
  }

  public async ngOnInit() {
    this.currentLang = this.translate.currentLang;
    this.subscribe(
      combineLatest([
        this.estimateService.observeCurrentEstimate(),
        this.estimateService.getGroupCostTypes(),
        this.currencyService.getCompanyCurrencies(),
        this.userService.getUser(),
        this.appSettingsService.getAppSettings()
      ])
        .pipe(
          switchMap(([estimate, costTypes, companyCurrencies, user, appSettings]) => {
            this.hasPC = appSettings.hasPC;
            this.currentEstimate = estimate;
            this.groupCostTypes = costTypes;
            this.companyCurrencies = companyCurrencies;
            const functionalCurrency = this.currencyService.findFunctionalCurrency(
              this.currentEstimate.companyCode,
              this.companyCurrencies
            );
            this.functionalCurrency = functionalCurrency.currencyCode;
            this.functionalCurrencySymbol = functionalCurrency.currency.symbol.trim();
            this.currentLang = user.languageCode ?? this.translate.currentLang;

            return this.estimateService.getClassDeptRates(this.currentEstimate.companyCode);
          })
        )
        .subscribe(classDeptRates => {
          this.averageRates = classDeptRates;
          this.isEstimateOnFunctionalCurrency =
            (this.currentEstimate.currencyCode ?? this.functionalCurrency) === this.functionalCurrency;

          this.onEstimateUpdate();
        })
    );

    this.subscribe(
      this.totalsData.currentProjectTotals$.subscribe(projectTotals => (this.projectTotals = projectTotals))
    );
    this.subscribe(
      this.currencySymbolService.getCurrentCurrencySymbol().subscribe(currencySymbol => {
        this.currencySymbol = currencySymbol;
      })
    );

    this.estimateConfig = await this.estimateSettingsService.getEstimateConfigurationAsync();
    this.activateInternalFees = this.estimateConfig.activateInternalFees;
    this.setIsPercentHidden();
  }

  private setIsPercentHidden() {
    this.isPercentHidden = {
      [TreeNodeType.Project]: false,
      [TreeNodeType.Level1]: this.estimateConfig.hideBudgetPercentLevel1,
      [TreeNodeType.Level2]: this.estimateConfig.hideBudgetPercentLevel2,
      [TreeNodeType.Department]: this.estimateConfig.hideBudgetPercentDepartment,
      [TreeNodeType.Category]: this.estimateConfig.hideBudgetPercentCategory,
      [TreeNodeType.GenericTime]: this.estimateConfig.hideBudgetPercentDepartment,
      [TreeNodeType.GenericExpense]: this.estimateConfig.hideBudgetPercentDepartment,
      [TreeNodeType.GenericInternalFee]: this.estimateConfig.hideBudgetPercentDepartment
    };
  }

  private updateCurrentEstimate() {
    this.setLowestLevels();
    this.isTableEditable = this.currentEstimate?.status === StatusEnum.New && !this.isGroupApproved;
  }

  private setLowestLevels() {
    if (this.currentEstimate.budgetType === BudgetTypeEnum.Summary) {
      this.lowestTimeLevel = this.departmentType;
      this.lowestInternalFeeLevel = this.departmentType;
      this.lowestExpenseLevel = this.departmentType;
    } else {
      if (this.currentEstimate.budgetTypeTime === BudgetTypeEnum.Summary) {
        this.lowestTimeLevel = this.genericTimeType;
      } else {
        this.lowestTimeLevel = this.categoryType;
      }

      if (this.currentEstimate.budgetTypeInternalFee === BudgetTypeEnum.Summary) {
        this.lowestInternalFeeLevel = this.genericInternalFeeType;
      } else {
        this.lowestInternalFeeLevel = this.categoryType;
      }

      if (this.currentEstimate.budgetTypeExpense === BudgetTypeEnum.Summary) {
        this.lowestExpenseLevel = this.genericExpenseType;
      } else {
        this.lowestExpenseLevel = this.categoryType;
      }
    }
  }

  private sortStructures() {
    this.currentEstimate.structures = SortHelper.sortStructures(this.currentEstimate.structures);
    this.currentEstimate.structures.forEach(structure => {
      structure.estimateLines.sort((a, b) => {
        if (a.category && b.category) {
          const aCat = a.category.code.toLowerCase();
          const bCat = b.category.code.toLowerCase();
          return aCat > bCat ? -1 : aCat < bCat ? 1 : 0;
        }
        if (a.departmentEstimate?.department && b.departmentEstimate?.department) {
          const aDep = a.departmentEstimate.department.code.toLowerCase(),
            bDep = b.departmentEstimate.department.code.toLowerCase();
          return aDep > bDep ? -1 : aDep < bDep ? 1 : 0;
        }
        return 0;
      });
    });
  }

  private async onEstimateUpdate() {
    this.sortStructures();
    this.classificationEstimates = SortHelper.sortClassificationEstimates(this.currentEstimate.classificationEstimates);
    this.internalResourceEstimates = SortHelper.sortInternalResourceEstimates(
      this.currentEstimate.internalResourceEstimates
    );
    //get current currency symbol
    if (this.currentEstimate.currencyCode) {
      this.subscribe(this.currencyService.getCurrencySymbol(this.currentEstimate.currencyCode, true).subscribe());
    }
    // get billing methods
    const { laborBillingMethodCode, internalFeeBillingMethodCode, expenseBillingMethodCode } = this.currentEstimate;
    const billingMethodObs = forkJoin({
      labor: laborBillingMethodCode
        ? this.estimateService.getLaborBillingMethods(laborBillingMethodCode)
        : of(undefined),
      internalFees: internalFeeBillingMethodCode
        ? this.estimateService.getInternalFeeBillingMethods(internalFeeBillingMethodCode)
        : of(undefined),
      expense: expenseBillingMethodCode
        ? this.estimateService.getExpenseBillingMethods(expenseBillingMethodCode)
        : of(undefined)
    });

    this.subscribe(
      billingMethodObs.subscribe(({ labor, internalFees, expense }) => {
        this.laborBillingMethods = labor;
        this.internalFeeBillingMethods = internalFees;
        this.expenseBillingMethods = expense;

        this.initTreeTable();
      })
    );
  }

  public initTreeTable() {
    this.estimateTree = [];
    this.setColumns();
    this.createTree();
    this.projectTotals = this.estimateTree[0].data.totals;
    this.totalsData.changeProjectTotals(this.projectTotals);
    this.totalRecords = TreeNodeHelper.count(this.estimateTree[0]);
    this.isLoaded = true;
  }

  public hasRate(classificationCode: string, departmentCode: string) {
    const result = this.averageRates.filter(
      e =>
        e?.classificationCode?.toUpperCase() === classificationCode?.toUpperCase() &&
        e.departmentCode === departmentCode
    );
    return result.length > 0;
  }

  public getClassesTooltips(rowData: TreeNodeData<EstimateLine>, classificationEstimate: ClassificationEstimate) {
    const departmentCode = rowData.entity?.departmentEstimate?.department.code;
    const classificationCode = classificationEstimate.classification?.code;

    if (!this.groupHasTime(rowData)) {
      return this.translate.instant('TOOLTIP.CATEGORY_GROUP') as string;
    }
    if (!this.hasRate(classificationCode, departmentCode)) {
      return this.translate.instant('TOOLTIP.NO_RATES') as string;
    }
    if (rowData.type === TreeNodeType.Category && !CostTypeHelper.isTimeCategory(rowData.entity.category)) {
      return this.translate.instant('TOOLTIP.COST_TYPE') as string;
    }

    let tooltip = '';

    let rate = 0;
    let sourceRate = 0;

    if (this.currentEstimate.status === StatusEnum.New) {
      // if new use rates table
      const averageRateForClass = this.averageRates.find(
        e =>
          e.classificationCode.toUpperCase() === classificationCode.toUpperCase() && e.departmentCode === departmentCode
      );
      rate = averageRateForClass.rate;
      sourceRate = averageRateForClass.sourceRate;
    } else {
      const estimateLine = rowData.entity;
      const classificationLine = estimateLine.classificationLines.find(
        l => l.classificationEstimateId === classificationEstimate.id
      );
      rate = classificationLine?.rateCost ?? 0;
      sourceRate = classificationLine?.rateCostDepartment ?? 0;
    }

    if (rate === 0 && sourceRate === 0) {
      // this is the case when the #hours is < 0
      return this.translate.instant('TOOLTIP.NO_RATES') as string;
    }

    const rateStr = this.translate.instant('TOOLTIP.RATE') as string;
    const rateFormatted = NumberHelper.format(rate);

    tooltip = `${rateStr}: ${rateFormatted} ${this.functionalCurrencySymbol ?? ''}`;

    const currency = rowData.totals.departmentCurrency;
    if (currency) {
      tooltip += `\n(${NumberHelper.format(sourceRate)} ${currency.currency.symbol?.trim()})`;
    }

    return tooltip;
  }

  public getInternalResourcesTooltips(
    rowData: TreeNodeData<EstimateLine>,
    internalResourceEstimate: InternalResource_Estimate
  ) {
    const internalResourceRate = internalResourceEstimate.internalResource?.rate;

    if (!this.groupHasInternalFee(rowData)) {
      return this.translate.instant('TOOLTIP.CATEGORY_GROUP') as string;
    }
    if (!internalResourceRate) {
      return this.translate.instant('TOOLTIP.NO_RATES') as string;
    }
    if (rowData.type === TreeNodeType.Category && !CostTypeHelper.isInternalFeeCategory(rowData.entity.category)) {
      return this.translate.instant('TOOLTIP.COST_TYPE') as string;
    }

    const rate =
      this.currentEstimate.status === StatusEnum.New
        ? internalResourceRate
        : (rowData.entity.internalFeeLines.find(l => l.internalResourceEstimateId === internalResourceEstimate.id)
            ?.rateCost ?? 0);

    if (rate === 0) {
      return this.translate.instant('TOOLTIP.NO_RATES') as string;
    }

    const rateStr = this.translate.instant('TOOLTIP.RATE') as string;
    const rateFormatted = NumberHelper.format(rate);

    return `${rateStr}: ${rateFormatted} ${this.functionalCurrencySymbol ?? ''}`;
  }

  public getHoursTooltips(rowData: TreeNodeData<EstimateLine>) {
    if (!this.groupHasTime(rowData)) {
      return this.translate.instant('TOOLTIP.CATEGORY_GROUP') as string;
    }
    if (this.hasPC && !rowData.totals.totalHours) {
      return this.translate.instant('TOOLTIP.MISSING_HOURS') as string;
    }
  }

  public getInternalFeesTooltips(rowData: TreeNodeData<EstimateLine>) {
    let tooltip = '';
    if (!this.groupHasInternalFee(rowData)) {
      tooltip = this.translate.instant('TOOLTIP.CATEGORY_GROUP') as string;
    }
    return tooltip;
  }

  public getExpensesTooltips(rowData: TreeNodeData<EstimateLine>) {
    let tooltip = '';
    if (rowData.type === TreeNodeType.Category && !CostTypeHelper.isExpenseCategory(rowData.entity.category)) {
      tooltip = this.translate.instant('TOOLTIP.COST_TYPE') as string;
    } else if (!this.groupHasExpense(rowData)) {
      tooltip = this.translate.instant('TOOLTIP.CATEGORY_GROUP') as string;
    }
    return tooltip;
  }

  public getExpenseBillableTooltips(rowData: TreeNodeData<EstimateLine>, currentTooltip: string) {
    if (isEmpty(currentTooltip)) {
      if (this.hasPC && !rowData.totals.totalExpensesCost) {
        return this.translate.instant('TOOLTIP.MISSING_COST') as string;
      }
    }
    return currentTooltip;
  }

  private groupHasTime(rowData: TreeNodeData<EstimateLine>) {
    if (rowData.type === this.lowestTimeLevel) {
      const estimateLine = rowData.entity;

      if (estimateLine.structure && estimateLine.structure.categoryGroup === null) return true;

      const groupCode = estimateLine.structure?.categoryGroup?.code;
      const result = this.groupCostTypes.find(g => g.categoryGroupCode === groupCode);

      return !!result?.hasTime;
    }
  }

  private groupHasInternalFee(rowData: TreeNodeData<EstimateLine>) {
    if (rowData.type === this.lowestInternalFeeLevel) {
      const estimateLine = rowData.entity;

      if (estimateLine.structure && estimateLine.structure.categoryGroup === null) return true;

      const groupCode = estimateLine.structure?.categoryGroup?.code;
      const result = this.groupCostTypes.find(g => g.categoryGroupCode === groupCode);

      return !!result?.hasInternalFee;
    }
  }

  private groupHasExpense(rowData: TreeNodeData<EstimateLine | Structure | Estimate>) {
    if (rowData.type === this.lowestExpenseLevel) {
      const estimateLine = rowData.entity as EstimateLine;

      if (estimateLine.structure && estimateLine.structure.categoryGroup === null) return true;

      const groupCode = estimateLine.structure?.categoryGroup?.code;
      const result = this.groupCostTypes.find(g => g.categoryGroupCode === groupCode);

      return !!result?.hasExpense;
    }
  }

  public classReadOnlyRules(rowData: TreeNodeData<EstimateLine>, classificationEstimate: ClassificationEstimate) {
    if (
      rowData.type === this.lowestTimeLevel &&
      rowData.type === TreeNodeType.Category &&
      !CostTypeHelper.isTimeCategory(rowData.entity.category)
    ) {
      return true;
    } else if (
      rowData.type === this.lowestTimeLevel &&
      !this.hasRate(
        classificationEstimate.classification?.code,
        rowData.entity && rowData.entity.departmentEstimate && rowData.entity.departmentEstimate.department.code
      )
    ) {
      return true;
    } else if (rowData.type !== this.lowestTimeLevel) {
      return true;
    } else if (this.groupCostTypes.length > 0) {
      return !this.groupHasTime(rowData);
    } else return false;
  }

  public intResourcesReadOnlyRules(rowData: TreeNodeData<EstimateLine>) {
    const notLowestLevel = rowData.type !== this.lowestInternalFeeLevel;
    const lowestFeeCategory =
      rowData.type === this.lowestInternalFeeLevel &&
      rowData.type === TreeNodeType.Category &&
      !CostTypeHelper.isInternalFeeCategory(rowData.entity.category);

    if (lowestFeeCategory || notLowestLevel) {
      return true;
    } else if (this.groupCostTypes.length) {
      return !this.groupHasInternalFee(rowData);
    }
    return false;
  }

  public expReadOnlyRules(rowData: TreeNodeData<EstimateLine>) {
    if (
      rowData.type === this.lowestExpenseLevel &&
      rowData.type === TreeNodeType.Category &&
      !CostTypeHelper.isExpenseCategory(rowData.entity.category)
    ) {
      return true;
    } else if (rowData.type !== this.lowestExpenseLevel) {
      return true;
    } else if (this.groupCostTypes.length > 0) {
      return !this.groupHasExpense(rowData);
    } else {
      return false;
    }
  }

  private removeZeroClass(type: TreeNodeType, lowestLevelType: TreeNodeType) {
    const genericTypes = [this.genericExpenseType, this.genericTimeType, this.genericInternalFeeType];
    const removeZeroTypes = [...genericTypes, lowestLevelType];

    if (
      (this.currentEstimate.budgetType === BudgetTypeEnum.Detailed &&
        this.currentEstimate.budgetTypeTime === BudgetTypeEnum.Summary &&
        type === TreeNodeType.Category) ||
      removeZeroTypes.includes(type)
    ) {
      return 'read-only remove-zero';
    } else {
      return 'parent-read-only';
    }
  }

  public removeZeroTime(rowData: TreeNodeData) {
    return this.removeZeroClass(rowData.type, this.lowestTimeLevel);
  }

  public removeZeroInternalFees(rowData: TreeNodeData) {
    return this.removeZeroClass(rowData.type, this.lowestInternalFeeLevel);
  }

  public removeZeroExpense(rowData: TreeNodeData) {
    return this.removeZeroClass(rowData.type, this.lowestExpenseLevel);
  }

  private isReInitTreeTableScheduled = false;
  public scheduledReInitTreeTable() {
    if (this.isReInitTreeTableScheduled) {
      return;
    }
    this.isReInitTreeTableScheduled = true;
    setTimeout(() => {
      try {
        if (!this.currentEstimate?.classificationEstimates) {
          return; // do nothing
        }

        this.initTreeTable();
      } finally {
        this.isReInitTreeTableScheduled = false;
      }
    }, 30);
  }

  public setColumns() {
    if (!this.currentEstimate?.classificationEstimates) {
      return; // do nothing
    }

    // Fees (Hours)
    this.feesColumns = [];
    if (!this.isClassColumnsCollapsed) {
      this.currentEstimate.classificationEstimates.forEach(() => {
        this.feesColumns.push(TreeTableColumnWidths.medium);
      });
    }
    this.feesColumns.push(
      TreeTableColumnWidths.small, // 1 hour col
      ...Array<TreeTableColumnWidths>(3).fill(TreeTableColumnWidths.default) // 3 default cols
    );

    // Internal Fees
    this.internalFeesColumns = [];
    if (!this.isInternalResourceColumnsCollapsed) {
      this.currentEstimate.internalResourceEstimates.forEach(() => {
        this.internalFeesColumns.push(TreeTableColumnWidths.medium);
      });
    }
    this.internalFeesColumns.push(
      TreeTableColumnWidths.small, // 1 quantity col
      ...Array<TreeTableColumnWidths>(3).fill(TreeTableColumnWidths.default) // 3 default cols
    );

    // Expense
    this.expenseColumns = Array<TreeTableColumnWidths>(3).fill(TreeTableColumnWidths.default); // 3 cols
    if (!this.isExpenseColumnCollapsed) {
      this.expenseColumns.push(TreeTableColumnWidths.default, TreeTableColumnWidths.default); // qnt+unitcost
    }

    // Totals
    const nbTotalColumns = this.sectionFilters.hideSectionBudgetPercentage ? 3 : 5;
    this.totalColumns = Array<TreeTableColumnWidths>(nbTotalColumns).fill(TreeTableColumnWidths.default); // 5 default cols (2 for %)

    if (!this.isEstimateOnFunctionalCurrency) {
      this.feesColumns.push(TreeTableColumnWidths.default, TreeTableColumnWidths.default); // billableCurrency, final billable functional currency
      this.internalFeesColumns.push(TreeTableColumnWidths.default, TreeTableColumnWidths.default); // billableCurrency, final billable functional currency
      this.expenseColumns.push(TreeTableColumnWidths.default, TreeTableColumnWidths.default); // billableCurrency, final billable functional currency
      this.totalColumns.push(TreeTableColumnWidths.default); // billableCurrency
    }

    // Expense icon (at the end)
    if (this.isTableEditable) this.expenseColumns.push(TreeTableColumnWidths.icon);

    // Hide columns if not in filters
    if (this.sectionFilters.hideSectionFees) {
      this.feesColumns = [];
    }
    if (this.sectionFilters.hideSectionInternalFees) {
      this.internalFeesColumns = [];
    }
    if (this.sectionFilters.hideSectionExpenses) {
      this.expenseColumns = [];
    }
  }

  public hasNotes(rowData: TreeNodeData<Estimate | Structure | EstimateLine>) {
    return this.getTreeNodeDetail(rowData, false)?.notes;
  }

  public toggleNoteDialog(
    event,
    rowData: TreeNodeData<Estimate | Structure | EstimateLine>,
    overlaypanel: OverlayPanel
  ) {
    const item = this.getTreeNodeDetail(rowData, false);
    this.selectedNote = item?.notes ?? '';

    this.selectedRowData = rowData;
    rowData.notes = this.selectedNote;
    overlaypanel.toggle(event);
  }

  public setNotes(rowData: TreeNodeData<Estimate | Structure | EstimateLine>) {
    const item = this.getTreeNodeDetail(rowData, true);

    item.notes = this.selectedNote;
    rowData.notes = this.selectedNote;

    this.estimateService.saveChanges().subscribe();
  }

  public selectHoursBillableFinal() {
    const billableFinalInput = document.getElementById('hoursBillableFinal') as HTMLInputElement;
    billableFinalInput.select();
  }

  public selectInternalFeesBillableFinal() {
    const billableFinalInput = document.getElementById('internalFeesBillableFinal') as HTMLInputElement;
    billableFinalInput.select();
  }

  public selectExpensesBillableFinal(rowData: TreeNodeData) {
    const isReadOnly = document.getElementById('expensesBillableFinal').className;
    if (isReadOnly.includes('read-only remove-zero') || rowData.type !== this.lowestExpenseLevel) {
      return false;
    } else {
      const expensesBillableFinalInput = document.getElementById('expensesBillableFinal') as HTMLInputElement;
      expensesBillableFinalInput.select();
    }
  }

  public selectExpenseBillable(rowData: TreeNodeData) {
    const isReadOnly = document.getElementById('expenseBillable').className;
    if (isReadOnly.includes('read-only remove-zero') || rowData.type !== this.lowestExpenseLevel) {
      return false;
    } else {
      const expenseBillableInput = document.getElementById('expenseBillable') as HTMLInputElement;
      expenseBillableInput.select();
    }
  }

  public selectNumbox(rowData: TreeNodeData) {
    const isReadOnly = document.getElementById('num-box').className;
    if (isReadOnly.includes('read-only remove-zero') || rowData.type !== this.lowestTimeLevel) {
      return false;
    } else {
      const numboxInput = document.getElementById('num-box') as HTMLInputElement;
      numboxInput.select();
    }
  }

  public hoursInputRules(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    return (rowData as TreeNodeData<EstimateLine>) ? !this.groupHasTime(rowData as TreeNodeData<EstimateLine>) : false;
  }

  public internalFeesInputRules(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    return (rowData as TreeNodeData<EstimateLine>)
      ? !this.groupHasInternalFee(rowData as TreeNodeData<EstimateLine>)
      : false;
  }

  private getSimpleRowType(rowType: TreeNodeType) {
    const isDepartmentType = [
      TreeNodeType.Department,
      TreeNodeType.GenericExpense,
      TreeNodeType.GenericTime,
      TreeNodeType.GenericInternalFee
    ].includes(rowType);
    return isDepartmentType ? this.departmentType : rowType;
  }

  private getParentNodeDetails(rowData: TreeNodeData) {
    //ORDER IS IMPORTANT
    // Category > Dep > Levl2 > lvl1 > Estimation

    const rowType = this.getSimpleRowType(rowData.type);

    const parentItems: TreeNodeDetail[] = [];
    switch (rowType) {
      case TreeNodeType.Project: {
        // no parents  :-(
        break;
      }

      case TreeNodeType.Level1: {
        parentItems.push(this.currentEstimate);
        break;
      }
      case TreeNodeType.Level2: {
        const structure = rowData.entity as Structure;
        const level1Detail = this.structureService.getLevel1DetailById(structure.level1Id, this.currentEstimate);
        parentItems.push(level1Detail);

        parentItems.push(this.currentEstimate);
        break;
      }
      case this.departmentType: {
        const estimateLine = rowData.entity as EstimateLine;

        const level2Detail = this.structureService.getLevel2DetailById(
          estimateLine.structure.level2Id,
          this.currentEstimate
        );
        parentItems.push(level2Detail);

        const level1Detail = this.structureService.getLevel1DetailById(
          estimateLine.structure.level1Id,
          this.currentEstimate
        );
        parentItems.push(level1Detail);
        parentItems.push(this.currentEstimate);
        break;
      }
      case this.categoryType: {
        const estimateLine = rowData.entity as EstimateLine;
        const deptDetail = this.structureService.getParentDepartmentDetail(estimateLine);
        parentItems.push(deptDetail);

        const level2Detail = this.structureService.getLevel2DetailById(
          estimateLine.structure.level2Id,
          this.currentEstimate
        );
        parentItems.push(level2Detail);

        const level1Detail = this.structureService.getLevel1DetailById(
          estimateLine.structure.level1Id,
          this.currentEstimate
        );
        parentItems.push(level1Detail);

        parentItems.push(this.currentEstimate);
      }
    }
    return parentItems;
  }

  private getChildrenNodeDetails(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    const rowType = this.getSimpleRowType(rowData.type);

    // Project > Level1 > Level2 > Department+Category
    const childItems: TreeNodeDetail[] = [];

    // if dateStart > child.dateStart
    switch (rowType) {
      case TreeNodeType.Project: {
        const estimate = rowData.entity as Estimate;
        for (const structure of estimate.structures) {
          childItems.push(this.structureService.getLevel1Detail(structure));
          childItems.push(this.structureService.getLevel2Detail(structure, this.currentEstimate));

          const departments = this.structureService.getDepartmentDetails(structure);
          childItems.push(...departments);
          const categories = this.structureService.getCategoryDetails(structure.estimateLines, this.currentEstimate);
          childItems.push(...categories);
        }
        break;
      }
      case this.level1Type: {
        const structure = rowData.entity as Structure;
        childItems.push(this.structureService.getLevel2Detail(structure, this.currentEstimate));

        const departments = this.structureService.getDepartmentDetails(structure);
        childItems.push(...departments);
        const categories = this.structureService.getCategoryDetails(structure.estimateLines, this.currentEstimate);
        childItems.push(...categories);
        break;
      }
      case this.level2Type: {
        const structure = rowData.entity as Structure;

        const departments = this.structureService.getDepartmentDetails(structure);
        childItems.push(...departments);
        const categories = this.structureService.getCategoryDetails(structure.estimateLines, this.currentEstimate);
        childItems.push(...categories);
        break;
      }
      case this.departmentType: {
        const estimateLine = rowData.entity as EstimateLine;

        const categories = this.structureService.getCategoryDetails([estimateLine], this.currentEstimate);
        childItems.push(...categories);
        break;
      }
    }
    return childItems;
  }

  private getTreeNodeDetail(
    rowData: TreeNodeData<Structure | Estimate | EstimateLine>,
    createIfNonExistant = false
  ): TreeNodeDetail {
    const rowType = this.getSimpleRowType(rowData.type);
    switch (rowType) {
      case this.projectType: {
        return this.currentEstimate;
      }
      case this.level1Type: {
        const lvl1 = rowData.entity as Structure;
        const level1Detail = this.structureService.getLevel1DetailById(lvl1.level1Id, this.currentEstimate);
        if (level1Detail) {
          return level1Detail;
        } else if (createIfNonExistant) {
          return this.estimateService.createLevel1Detail(lvl1.id);
        }
      }
      case this.level2Type: {
        const lvl2 = rowData.entity as Structure;
        if (lvl2.level2Detail) {
          return lvl2.level2Detail;
        } else if (createIfNonExistant) {
          return this.estimateService.createLevel2Detail(lvl2.id);
        }
      }
      case this.departmentType: {
        const estimateLine = rowData.entity as EstimateLine;
        const departmentDetail = estimateLine.departmentDetail;
        if (departmentDetail) {
          return departmentDetail;
        } else if (createIfNonExistant) {
          return this.estimateService.createDepartmentDetail(estimateLine.id);
        }
      }
      case this.categoryType: {
        const estimateLine = rowData.entity as EstimateLine;
        if (estimateLine.categoryDetail) {
          return estimateLine.categoryDetail;
        } else if (createIfNonExistant) {
          return this.estimateService.createCategoryDetail(estimateLine.id);
        }
      }
    }
  }

  private isStartDateOutOfRange(
    rowData: TreeNodeData<Structure | Estimate | EstimateLine>,
    newStartDate: Date
  ): boolean {
    // Project > Level1 > Level2 > Department > Category
    const childrenNodeDetails = this.getChildrenNodeDetails(rowData);
    const isChildOutOfRange = childrenNodeDetails.some(child => child && newStartDate > child.dateStart);

    // Category > Department > Level 2 > Level 1 > Project
    const parentsNodeDetails = this.getParentNodeDetails(rowData);
    const isParentOutOfRange = parentsNodeDetails.some(parent => parent && newStartDate < parent.dateStart);

    return isParentOutOfRange || isChildOutOfRange;
  }

  private setDateStartOutOfRange(rowData: TreeNodeData<Structure | Estimate | EstimateLine>, newStartDate: Date) {
    const item = this.getTreeNodeDetail(rowData, true);
    item.dateStart = newStartDate;
    rowData.startDate = newStartDate;

    // Project > Level1 > Level2 > Department > Category
    const childrenNodeDetails = this.getChildrenNodeDetails(rowData);
    const isChildOutOfRange = childrenNodeDetails.some(child => child && newStartDate > child.dateStart);

    // Category > Department > Level 2 > Level 1 > Project
    const parentsNodeDetails = this.getParentNodeDetails(rowData);
    const isParentOutOfRange = parentsNodeDetails.some(parent => parent && newStartDate < parent.dateStart);

    // if newStartDate < child.dateStart
    if (isChildOutOfRange) {
      // set new start date only on children that are out of range
      childrenNodeDetails
        .filter(child => !!child && newStartDate > child.dateStart)
        .forEach(child => (child.dateStart = newStartDate));
    }

    // if newStartDate > parent.dateStart
    if (isParentOutOfRange) {
      parentsNodeDetails
        .filter(parent => !!parent && newStartDate < parent.dateStart)
        .forEach(parent => (parent.dateStart = newStartDate));

      // check whole tree
      const allNodeDetails = this.getAllTreeNodeDetails();

      allNodeDetails
        .filter(node => !!node && newStartDate > node.dateStart)
        .forEach(node => (node.dateStart = newStartDate));
    }
  }

  private isEndDateOutOfRange(rowData: TreeNodeData<Structure | Estimate | EstimateLine>, newEndDate: Date): boolean {
    // Project > Level1 > Level2 > Department > Category
    const childrenNodeDetails = this.getChildrenNodeDetails(rowData);
    const isChildOutOfRange = childrenNodeDetails.some(child => child && newEndDate < child.dateEnd);

    // Category > Department > Level 2 > Level 1 > Project
    const parentsNodeDetails = this.getParentNodeDetails(rowData);
    const isParentOutOfRange = parentsNodeDetails.some(parent => parent && newEndDate > parent.dateEnd);

    return isParentOutOfRange || isChildOutOfRange;
  }

  private setDateEndOutOfRange(rowData: TreeNodeData<Structure | Estimate | EstimateLine>, newEndDate: Date) {
    const treeNodeDetail = this.getTreeNodeDetail(rowData, true);
    treeNodeDetail.dateEnd = newEndDate;
    rowData.endDate = newEndDate;

    // Project > Level1 > Level2 > Department > Category
    const childrenNodeDetails = this.getChildrenNodeDetails(rowData);
    const isChildOutOfRange = childrenNodeDetails.some(child => child && newEndDate < child.dateEnd);

    // Category > Department > Level 2 > Level 1 > Project
    const parentsNodeDetails = this.getParentNodeDetails(rowData);
    const isParentOutOfRange = parentsNodeDetails.some(parent => parent && newEndDate > parent.dateEnd);

    // if newDateEnd < child.dateEnd
    if (isChildOutOfRange) {
      // set new end date only on children that are out of range
      childrenNodeDetails
        .filter(child => !!child && newEndDate < child.dateEnd)
        .forEach(child => (child.dateEnd = newEndDate));
    }

    // if newDateEnd > parent.dateEnd
    if (isParentOutOfRange) {
      parentsNodeDetails
        .filter(parent => !!parent && newEndDate > parent.dateEnd)
        .forEach(parent => (parent.dateEnd = newEndDate));

      // check whole tree
      const allNodeDetails = this.getAllTreeNodeDetails();

      allNodeDetails.filter(node => !!node && newEndDate < node.dateEnd).forEach(node => (node.dateEnd = newEndDate));
    }
  }

  private getAllTreeNodeDetails() {
    const allNodeDetails: TreeNodeDetail[] = [this.currentEstimate];
    for (const structure of this.currentEstimate.structures) {
      // Level1 > Level2 > Department > Category
      allNodeDetails.push(this.structureService.getLevel1Detail(structure));
      allNodeDetails.push(this.structureService.getLevel2Detail(structure, this.currentEstimate));

      const departments = this.structureService.getDepartmentDetails(structure);
      allNodeDetails.push(...departments);

      const categories = this.structureService.getCategoryDetails(structure.estimateLines, this.currentEstimate);
      allNodeDetails.push(...categories);
    }

    return allNodeDetails;
  }

  /**
   * The only time that Estimate.DateStart === null is if we are creating an estimate from a CRM Budget Reallocation Opportunity
   * In that case, if we have Level 1s, we should have already created Level1Details based on the start/end dates from Sifia
   * We will use the "lowest" date and if none are available, today's date
   * */
  private getProjectStartDate() {
    if (this.currentEstimate.dateStart) {
      return this.currentEstimate.dateStart;
    } else {
      const level1Details = <Level1Detail[]>[];
      for (const structure of this.currentEstimate.structures) {
        if (structure.level1Detail) level1Details.push(structure.level1Detail);
      }

      const datesAsc = SortHelper.sortArrayByProperty(level1Details, o => o.dateStart);

      this.currentEstimate.dateStart = datesAsc.length > 0 ? datesAsc[0].dateStart : DateTime.now().toJSDate();
      return this.currentEstimate.dateStart;
    }
  }

  /**
   * The only time that Estimate.DateEnd === null is if we are creating an estimate from a CRM Budget Reallocation Opportunity
   * In that case, if we have Level 1s, we should have already created Level1Details based on the start/end dates from Sifia
   * We will use the "highest" date and if none are available, today's date
   * */
  private getProjectEndDate() {
    if (this.currentEstimate.dateEnd) {
      return this.currentEstimate.dateEnd;
    } else {
      const level1Details = <Level1Detail[]>[];
      for (const structure of this.currentEstimate.structures) {
        if (structure.level1Detail) level1Details.push(structure.level1Detail);
      }

      const datesDesc = SortHelper.sortArrayByProperty(level1Details, o => o.dateEnd, true);

      this.currentEstimate.dateEnd = datesDesc.length > 0 ? datesDesc[0].dateEnd : DateTime.now().toJSDate();
      return this.currentEstimate.dateEnd;
    }
  }

  public getDateStart(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    let dateStart = this.getTreeNodeDetail(rowData, false)?.dateStart;
    if (dateStart) {
      return dateStart;
    }

    dateStart = this.getMinStartDate(rowData);
    rowData.startDate = dateStart; // TODO WTF?

    return dateStart;
  }

  public async setDateStart(rowData: TreeNodeData<Structure | Estimate | EstimateLine>, dateStart: Date) {
    if (dateStart === rowData.startDate) {
      return; // do nothing
    }
    const needconfirmation = this.isStartDateOutOfRange(rowData, dateStart);

    const doIt = async () => {
      this.setDateStartOutOfRange(rowData, dateStart);
      await this.estimateService.saveChangesAsync();
      await this.estimateService.refreshEstimateAsync();
    };

    if (!needconfirmation) {
      await doIt();
    } else {
      this.confirmationService.confirm({
        key: 'global-confirmation-dialog',
        message: this.translate.instant('ERROR.DATE') as string,
        header: this.translate.instant('HEADER.MODIFY_DATE') as string,
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
        rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
        accept: async () => {
          await doIt();
        }
      });
    }
  }

  public getMinStartDate(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    const parents = this.getParentNodeDetails(rowData);

    return (
      parents.find(parent => parent !== null && parent !== undefined)?.dateStart ??
      this.getProjectStartDate() ??
      this.currentEstimate.dateStart
    );
  }

  public getMaxEndDate(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    const parents = this.getParentNodeDetails(rowData);

    return (
      parents.find(parent => parent !== null && parent !== undefined)?.dateEnd ??
      this.getProjectEndDate() ??
      this.currentEstimate.dateEnd
    );
  }

  public getDateEnd(rowData: TreeNodeData<Structure | Estimate | EstimateLine>) {
    let dateEnd = this.getTreeNodeDetail(rowData, false)?.dateEnd;
    if (dateEnd) {
      return dateEnd;
    }

    dateEnd = this.getMaxEndDate(rowData);
    rowData.endDate = dateEnd; // TODO WTF?

    return dateEnd;
  }

  public async setDateEnd(rowData: TreeNodeData<Structure | Estimate | EstimateLine>, dateEnd: Date) {
    if (dateEnd === rowData.endDate) {
      return; // do nothing
    }

    const needConfirmation = this.isEndDateOutOfRange(rowData, dateEnd);

    const doIt = async () => {
      this.setDateEndOutOfRange(rowData, dateEnd);
      await this.estimateService.saveChangesAsync();
      await this.estimateService.refreshEstimateAsync();
    };

    if (!needConfirmation) {
      await doIt();
    } else {
      this.confirmationService.confirm({
        key: 'global-confirmation-dialog',
        message: this.translate.instant('ERROR.DATE') as string,
        header: this.translate.instant('HEADER.MODIFY_DATE') as string,
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
        rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
        accept: async () => {
          await doIt();
        }
      });
    }
  }

  private acceptChanges(event: TreeTableEditEvent) {
    const column = event.data;
    const row = event.field as unknown as TreeNodeContainer;
    const key = column as keyof TreeNodeTotals;
    const expenseCostColumns: (keyof TreeNodeTotals)[] = [
      'expenseCostQuantity',
      'expenseCostUnitCost',
      'totalExpensesCost'
    ];

    if (expenseCostColumns.includes(key)) {
      const node = row.node as TreeNode<TreeNodeData<EstimateLine>>;
      const rowData = node.data;
      const isUserInputUndefined = rowData.totals[key] === undefined;
      if (this.expReadOnlyRules(rowData) || isUserInputUndefined) {
        return true;
      }

      const lockKey = rowData.entity.id + 'expenseLine';
      this.executeIfLockIsAvailable(lockKey, () =>
        from(this.expenseService.sendChanges(rowData.totals, rowData.entity)).pipe(
          map(() => {
            this.propagateTotals(node);
            this.totalsData.change();
          })
        )
      );

      return true;
    }
    return false;
  }

  public async onEditComplete(event: TreeTableEditEvent) {
    const column = event.data;
    const node = (event.field as unknown as TreeNodeContainer).node as TreeNode<TreeNodeData<EstimateLine>>;
    const rowData = node.data;

    if (await this.acceptChanges(event)) {
      return;
    }

    switch (column) {
      case 'expensesBillable': {
        if (!this.expReadOnlyRules(rowData)) this.editExpensesBillable(node);
        break;
      }
      case 'hoursBillableFinal': {
        this.editHoursBillableFinal(node);
        break;
      }
      case 'internalFeesBillableFinal': {
        this.editInternalFeesBillableFinal(node);
        break;
      }
      case 'expenseBillableFinal': {
        if (!this.expReadOnlyRules(rowData)) this.editExpensesBillableFinal(node);
        break;
      }
      default: {
        const entityTypeName = (column as unknown as Entity).entityType?.shortName;

        if (entityTypeName === 'ClassificationEstimate') {
          this.editHours(node, column as unknown as ClassificationEstimate);
        } else if (entityTypeName === 'InternalResource_Estimate') {
          this.editInternalResourceQuantity(node, column as unknown as InternalResource_Estimate);
        }
        break;
      }
    }
  }

  public getClassificationLine(rowData: TreeNodeData<EstimateLine>, classificationEstimate: ClassificationEstimate) {
    return rowData.entity.classificationLines?.find(cl => cl.classificationEstimateId === classificationEstimate.id);
  }

  public anyClassLineHasHours(rowData: TreeNodeData<EstimateLine>) {
    return rowData.entity.classificationLines?.some(cl => cl.hours !== 0);
  }

  public getInternalFeeLine(rowData: TreeNodeData<EstimateLine>, internalResourceEstimate: InternalResource_Estimate) {
    return rowData.entity.internalFeeLines?.find(il => il.internalResourceEstimateId === internalResourceEstimate.id);
  }

  public isStartDateDisabled(rowData: TreeNodeData<Structure>) {
    const isReallocOrAdditionalServ =
      this.currentEstimate.estimateType === EstimateTypeEnum.Reallocation ||
      this.currentEstimate.estimateType === EstimateTypeEnum.AdditionalService;

    if (!isReallocOrAdditionalServ) {
      return false;
    }

    switch (rowData.type) {
      case TreeNodeType.Project:
        return true;
      case TreeNodeType.Level1:
        return !rowData.entity.level1.isNew;
      case TreeNodeType.Level2:
        return !rowData.entity.level2.isNew;
    }
  }

  public refreshDateColspan() {
    this.dateColspan = this.areDatesHidden ? 0 : 2;
  }

  private createTree() {
    const rootNode = this.createNode(
      this.currentEstimate.projectCode,
      this.currentEstimate.projectName,
      this.currentEstimate,
      TreeNodeType.Project
    );

    this.estimateTree = [rootNode, ...this.estimateTree];

    for (const structure of this.currentEstimate.structures) {
      let level1NodeExists = false;

      if (structure.level1) {
        for (const level1Node of this.estimateTree[0].children as TreeNode<TreeNodeData<Structure>>[]) {
          // if a level1Node already exists for this level1
          if (structure.level1.code === level1Node.data.entity.level1.code) {
            level1NodeExists = true;
            this.addLevel1Node(structure, level1Node);
            // when found, exit loop
            break;
          }
        }
        if (!level1NodeExists) {
          this.addLevel1Node(structure);
        }
      }
    }

    for (const child of this.estimateTree[0].children) {
      this.estimateTree[0].data.totals.addToTotals(child.data.totals);
    }

    //when tree is created, get the expanded value for each node stored during a session
    for (let i = 0; i < this.estimateTree.length; i++) {
      //expand project by default
      this.estimateTree[0].expanded = true;
      const estimateTreeLevel1Nodes = this.estimateTree[0].children as TreeNode<TreeNodeData<Structure>>[];
      for (const level1Node of estimateTreeLevel1Nodes) {
        //for every level1Node, check if id is locally stored
        const value = sessionStorage.getItem(level1Node.data.entity.level1Id);
        level1Node.expanded = value === 'true';
        //for every children of level1Node, check if id is locally stored
        for (const level2Node of level1Node.children) {
          const value = sessionStorage.getItem(level2Node.data.entity.level2Id);
          level2Node.expanded = value === 'true';
          //for every children of level2Node, check if id is locally stored
          for (const level3Node of level2Node.children) {
            const value = sessionStorage.getItem(level3Node.data.entity.id);
            level3Node.expanded = value === 'true';
          }
        }
      }
    }
  }

  public nodeExpand(event) {
    //stores the value if node is expanded
    const node = event.node as TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>;
    this.setTreeNodeExpandStatus(node, true);
  }

  public nodeCollapse(event) {
    //stores the value if node is collapsed
    const node = event.node as TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>;
    this.setTreeNodeExpandStatus(node, false);
  }

  private setTreeNodeExpandStatus(
    node: TreeNode<TreeNodeData<Structure | Estimate | EstimateLine>>,
    isExpanded: boolean
  ) {
    let id = node.data.entity.id;
    if (node.data.type === TreeNodeType.Level1) {
      const data = node.data as TreeNodeData<Structure>;
      id = data.entity.level1Id;
    } else if (node.data.type === TreeNodeType.Level2) {
      const data = node.data as TreeNodeData<Structure>;
      id = data.entity.level2Id;
    }
    sessionStorage.setItem(id, isExpanded.toString());
  }

  private setTreeExpandStatus(isExpanded: boolean) {
    const isExpandedStr = isExpanded.toString();
    this.initTreeTable();
    this.estimateTree[0].expanded = isExpanded;
    const estimateTreeLevel1Nodes = this.estimateTree[0].children as TreeNode<TreeNodeData<Structure>>[];
    for (const level1Node of estimateTreeLevel1Nodes) {
      level1Node.expanded = isExpanded;
      sessionStorage.setItem(level1Node.data.entity.level1Id, isExpandedStr);

      for (const level2Node of level1Node.children) {
        level2Node.expanded = isExpanded;
        sessionStorage.setItem(level2Node.data.entity.level2Id, isExpandedStr);

        for (const level3Node of level2Node.children) {
          level3Node.expanded = isExpanded;
          sessionStorage.setItem(level3Node.data.entity.id, isExpandedStr);
        }
      }
    }
    this.isExpanded = isExpanded;
    this.isCollapsed = !isExpanded;
  }

  public expandAll() {
    this.setTreeExpandStatus(true);
  }

  public collapseAll() {
    this.setTreeExpandStatus(false);
  }

  private addLevel1Node(structure: Structure, level1Node?: TreeNode<TreeNodeData<Structure>>) {
    let newLevel1Node: TreeNode<TreeNodeData<Structure>> = null;

    // if no level1Node given, create new Level 1 Node
    if (level1Node) {
      newLevel1Node = level1Node;
    } else {
      newLevel1Node = this.createNode(
        structure.level1.code,
        structure.level1.description,
        structure,
        TreeNodeType.Level1,
        this.estimateTree[0]
      );
    }

    // add Departments or Level2s to Level1
    if (this.currentEstimate.structureDepth === StructureDepthEnum.Level1) {
      this.addDeptNodes(newLevel1Node, structure);
    } else if (this.currentEstimate.structureDepth === StructureDepthEnum.Level2) {
      if (structure.level2) {
        this.addLevel2Node(newLevel1Node, structure);
      }
    }

    // update estimateTree
    if (level1Node) {
      level1Node = newLevel1Node;
    } else {
      /*
      PRIMENG: Change Detection
        TreeTable may need to be aware of changes in its value in some cases.
        For the sake of performance, this is only done when the reference of the
        value changes meaning a setter is used instead of ngDoCheck/IterableDiffers
        which can reduce performance. So when you manipulate the value such as removing a node,
        adding a node or changing children of a node, instead of using array methods such as push,
        splice create a new array reference using a spread operator or similar.
      */
      this.estimateTree[0].children = [...this.estimateTree[0].children, newLevel1Node];
    }
  }

  private addLevel2Node(level1Node: TreeNode<TreeNodeData<Structure>>, structure: Structure) {
    // create level2 node
    const level2Node = this.createNode(
      structure.level2.code,
      structure.level2.description,
      structure,
      TreeNodeType.Level2,
      level1Node
    );

    this.addDeptNodes(level2Node, structure);

    level1Node.data.totals.addToTotals(level2Node.data.totals);

    // add to level1 node
    //do not refactor to .push() method. see addLevel1Node() method for explanation.
    level1Node.children = [...level1Node.children, level2Node];
  }

  private isDepartmentHidden(estimateLine?: EstimateLine) {
    const department = estimateLine?.departmentEstimate?.department;
    if (!department) return false;

    const departmentId = department.id;
    if (!departmentId) {
      return true;
    }
    return !sessionStorage.getItem(departmentId);
  }

  private addDeptNodes(parentNode: TreeNode<TreeNodeData<Estimate | Structure | EstimateLine>>, structure: Structure) {
    // get all Estimate Line entities where the category is null; for each Department Estimate added to a Structure, there should be an Estimate Line created without a Category
    const departmentLines = structure.estimateLines.filter(e => e.category === null);

    const filteredDepartmentLines = departmentLines.filter(dl => this.isDepartmentHidden(dl));

    // for each Estimate Line, create a department node
    for (const deptEstimateLine of filteredDepartmentLines) {
      let departmentCurrency: CompanyCurrency | undefined;

      const department = deptEstimateLine.departmentEstimate.department;
      if (department.company !== this.currentEstimate.companyCode) {
        //Get the department's company's functional currency
        const currencyCode = this.currencyService.findFunctionalCurrency(
          department.company,
          this.companyCurrencies
        ).currencyCode;

        //Get the department currency the curency from the prespective of the current company
        departmentCurrency = this.companyCurrencies.find(
          c => c.companyCode === this.currentEstimate.companyCode && c.currencyCode === currencyCode
        );
      }

      const deptNode = this.createNode(
        department.code,
        department.description,
        deptEstimateLine,
        TreeNodeType.Department,
        parentNode,
        departmentCurrency
      );

      // if BudgetTypeEnum.Detailed, we need to create category nodes for each department
      if (this.currentEstimate.budgetType === BudgetTypeEnum.Detailed) {
        // get all Estimate Line entities that match the current department and have a category
        const categoryLines = structure.estimateLines.filter(
          e => e.departmentEstimateId === deptEstimateLine.departmentEstimateId && e.category !== null
        );

        for (const catLine of categoryLines) {
          this.addCategoryNode(deptNode, catLine);
        }

        // add generic time or expense nodes where BudgetTypeEnum.Summary
        if (this.currentEstimate.budgetTypeTime === BudgetTypeEnum.Summary) {
          this.addGenericTimeNode(deptNode);
        }
        if (this.currentEstimate.budgetTypeExpense === BudgetTypeEnum.Summary) {
          this.addGenericExpenseNode(deptNode);
        }
        if (this.currentEstimate.budgetTypeInternalFee === BudgetTypeEnum.Summary && this.activateInternalFees) {
          this.addGenericInternalFeeNode(deptNode);
        }
      } else {
        // only setTotals(...) when the node is a 'leaf'; in this case if BudgetTypeEnum.Summary, the department is the leaf
        deptNode.data.totals.setTotals(this.currentEstimate, deptEstimateLine, this.functionalCurrency, this.hasPC);
      }

      // add new department node to parent node
      parentNode.children = [deptNode, ...parentNode.children];
      // add department node TreeNodeTotals to parent node TreeNodeTotals
      parentNode.data.totals.addToTotals(deptNode.data.totals);
    }
  }

  private addGenericTimeNode(departmentNode: TreeNode<TreeNodeData<EstimateLine>>) {
    const code = '#T';
    const description = this.translate.instant('GENERICTIME') as string;
    const entity = departmentNode.data.entity;

    const genericTimeNode = this.createNode(code, description, entity, TreeNodeType.GenericTime, departmentNode);

    if (this.groupHasTime(genericTimeNode.data)) {
      genericTimeNode.data.totals.setTotals(
        this.currentEstimate,
        entity,
        this.functionalCurrency,
        this.hasPC,
        true,
        false
      );
      departmentNode.data.totals.addToTotals(genericTimeNode.data.totals);

      departmentNode.children = [genericTimeNode, ...departmentNode.children];
    }
  }

  private addGenericInternalFeeNode(departmentNode: TreeNode<TreeNodeData<EstimateLine>>) {
    const code = '#F';
    const description = this.translate.instant('GENERICINTERNALFEE') as string;
    const entity = departmentNode.data.entity;

    const genericInternalFeeNode = this.createNode(
      code,
      description,
      entity,
      TreeNodeType.GenericInternalFee,
      departmentNode
    );

    if (this.groupHasInternalFee(genericInternalFeeNode.data)) {
      genericInternalFeeNode.data.totals.setTotals(
        this.currentEstimate,
        entity,
        this.functionalCurrency,
        this.hasPC,
        false,
        true,
        false
      );
      departmentNode.data.totals.addToTotals(genericInternalFeeNode.data.totals);

      departmentNode.children = [genericInternalFeeNode, ...departmentNode.children];
    }
  }

  private addGenericExpenseNode(departmentNode: TreeNode<TreeNodeData<EstimateLine>>) {
    const code = '#E';
    const description = this.translate.instant('GENERICEXPENSE') as string;
    const entity = departmentNode.data.entity;

    const genericExpenseNode = this.createNode(code, description, entity, TreeNodeType.GenericExpense, departmentNode);

    if (this.groupHasExpense(genericExpenseNode.data)) {
      genericExpenseNode.data.totals.setTotals(
        this.currentEstimate,
        entity,
        this.functionalCurrency,
        this.hasPC,
        false,
        false,
        true
      );
      departmentNode.data.totals.addToTotals(genericExpenseNode.data.totals);

      departmentNode.children = [genericExpenseNode, ...departmentNode.children];
    }
  }

  private addCategoryNode(departmentNode: TreeNode<TreeNodeData<unknown>>, estimateLine: EstimateLine) {
    const categoryNode = this.createNode(
      estimateLine.category.code,
      estimateLine.category.description,
      estimateLine,
      this.categoryType,
      departmentNode
    );

    categoryNode.data.totals.setTotals(this.currentEstimate, estimateLine, this.functionalCurrency, this.hasPC);
    departmentNode.data.totals.addToTotals(categoryNode.data.totals);

    departmentNode.children = [categoryNode, ...departmentNode.children];
  }

  public isEstimateLineBusy(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const estimateLineId = node.data.entity.id;
    return Object.keys(this.isBusy).some(k => k.includes(estimateLineId));
  }

  /**
   * PRIMENG p-treeTable uses the following TreeNode properties only => data?: any; children?: TreeNode[]; leaf?: boolean; expanded?: boolean;
   * @param code
   * @param description
   * @param data
   * @param type
   * @param parent
   */
  private createNode<T extends EstimateLine | Structure | Estimate>(
    code: string,
    description: string,
    data: T,
    type: TreeNodeType,
    parent?: TreeNode,
    departmentCurrency?: CompanyCurrency
  ): TreeNode<TreeNodeData<T>> {
    code = code || '---';
    description = description || '---';

    const label =
      type === TreeNodeType.GenericTime ||
      type === TreeNodeType.GenericInternalFee ||
      type === TreeNodeType.GenericExpense
        ? description
        : code + ' \u2022 ' + description;

    const billingMethods = {
      labor: this.laborBillingMethods,
      internalFees: this.internalFeeBillingMethods,
      expense: this.expenseBillingMethods
    };

    const rowData = {
      entity: data,
      label: label,
      type,
      totals: new TreeNodeTotals(this.currentEstimate, billingMethods, this.averageRates, departmentCurrency)
    } as TreeNodeData<T>;

    const treeNode: TreeNode<TreeNodeData<T>> = {
      data: rowData,
      children: [],
      parent
    };

    const item = this.getTreeNodeDetail(rowData, false);
    rowData.notes = item?.notes;
    rowData.startDate = this.getDateStart(rowData);
    rowData.endDate = this.getDateEnd(rowData);

    if (type !== this.lowestExpenseLevel) {
      rowData.totals.expenseCostQuantity = undefined;
      rowData.totals.expenseCostUnitCost = undefined;
      rowData.totals.totalExpensesCost = 0;
      rowData.totals.totalExpensesBillable = 0;
      rowData.totals.totalExpensesBillableCurrency = 0;
      rowData.totals.totalExpensesBillableFinal = 0;
    }

    return treeNode;
  }

  public editHours(node: TreeNode<TreeNodeData<EstimateLine>>, classificationEstimate: ClassificationEstimate) {
    const lockKey = node.data.entity.id + classificationEstimate.id;

    if (node.data.totals.classificationTotals[classificationEstimate.id] === null) {
      this.removeHours(node, classificationEstimate);
      return;
    }

    this.executeIfLockIsAvailable(lockKey, () => {
      const rowData = node.data;

      let hours = Number(rowData.totals.classificationTotals[classificationEstimate.id]);
      if (isNaN(hours)) {
        rowData.totals.classificationTotals[classificationEstimate.id] = 0;
        hours = 0;
      }

      const classificationLine = this.getClassificationLine(node.data, classificationEstimate);

      if (classificationLine) {
        classificationLine.hours = hours;
      } else {
        const classId = classificationEstimate.classification.code;
        const deptId = rowData.entity.departmentEstimate.department.code;

        const avgRateCost =
          this.averageRates.find(
            r => r.classificationCode.toUpperCase() === classId.toUpperCase() && r.departmentCode === deptId
          )?.rate ?? 0;

        this.estimateService.createClassificationLine(rowData.entity.id, classificationEstimate.id, hours, avgRateCost);
      }

      if (rowData.entity.laborBillableFinal !== null) {
        this.clearHoursFinalValue(node);
      }

      return this.saveChangesAndUpdateTotals(node);
    });
  }

  public removeHours(rowData: TreeNode<TreeNodeData<EstimateLine>>, classificationEstimate: ClassificationEstimate) {
    const lockKey = rowData.data.entity.id + classificationEstimate.id;
    this.executeIfLockIsAvailable(lockKey, () => {
      const classificationLine = this.getClassificationLine(rowData.data, classificationEstimate);
      if (!classificationLine) {
        return EMPTY;
      }

      rowData.data.totals.classificationTotals[classificationEstimate.id] = null;
      return this.estimateService
        .deleteClassificationLine(classificationLine.id)
        .pipe(map(() => this.propagateTotals(rowData)));
    });
  }

  public editInternalResourceQuantity(
    node: TreeNode<TreeNodeData<EstimateLine>>,
    internalResourceEstimate: InternalResource_Estimate
  ) {
    const lockKey = node.data.entity.id + internalResourceEstimate.id;

    if (node.data.totals.internalResourceTotals[internalResourceEstimate.id] === null) {
      this.removeInternalResourceQuantity(node, internalResourceEstimate);
      return;
    }

    this.executeIfLockIsAvailable(lockKey, () => {
      const rowData = node.data;

      let quantity = Number(rowData.totals.internalResourceTotals[internalResourceEstimate.id]);
      if (isNaN(quantity)) {
        rowData.totals.internalResourceTotals[internalResourceEstimate.id] = 0;
        quantity = 0;
      }

      const internalFeeLine = this.getInternalFeeLine(node.data, internalResourceEstimate);

      if (internalFeeLine) {
        internalFeeLine.quantity = quantity;
      } else {
        const rate = internalResourceEstimate.internalResource.rate;
        this.estimateService.createInternalFeeLine(rowData.entity.id, internalResourceEstimate.id, quantity, rate);
      }

      if (rowData.entity.internalFeeBillableFinal !== null) {
        this.clearInternalFeesFinalValue(node);
      }

      return this.saveChangesAndUpdateTotals(node);
    });
  }

  public removeInternalResourceQuantity(
    rowData: TreeNode<TreeNodeData<EstimateLine>>,
    internalResourceEstimate: InternalResource_Estimate
  ) {
    const lockKey = rowData.data.entity.id + internalResourceEstimate.id;
    this.executeIfLockIsAvailable(lockKey, () => {
      const internalFeeLine = this.getInternalFeeLine(rowData.data, internalResourceEstimate);
      if (!internalFeeLine) {
        return EMPTY;
      }

      return this.estimateService.deleteInternalFeeLine(internalFeeLine.id).pipe(
        map(() => {
          rowData.data.totals.internalResourceTotals[internalResourceEstimate.id] = null;
          this.propagateTotals(rowData);
        })
      );
    });
  }

  public editExpensesBillable(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const lockKey = node.data.entity.id + 'expenseLine';
    this.executeIfLockIsAvailable(lockKey, () => {
      const rowData = node.data;
      const expenseLine = rowData.entity.expenseLine;
      const userInput = Number(rowData.totals.totalExpensesBillable);

      if (!userInput || isNaN(userInput)) {
        return EMPTY;
      }

      if (expenseLine) {
        expenseLine.amountBillable = userInput;
      } else {
        this.estimateService.createExpenseLine(rowData.entity.id, {
          amountBillable: userInput
        });
      }

      return this.saveChangesAndUpdateTotals(node);
    });
  }

  public removeExpenses(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const lockKey = node.data.entity.id + 'expenseLine';
    this.executeIfLockIsAvailable(lockKey, () => {
      const rowData = node.data;
      const expenseLine: ExpenseLine = rowData.entity.expenseLine;
      if (expenseLine) {
        return from(this.expenseService.deleteLine(expenseLine.estimateLineId)).pipe(
          map(() => this.propagateTotals(node))
        );
      }
    });
  }

  //Why use this method ?
  //When Breeze can concurrently call savechanges while you are also sending api calls manually.
  private executeIfLockIsAvailable(lockKey: string, action: () => Observable<unknown>) {
    if (this.isBusy[lockKey]) {
      return;
    }

    //lock
    this.isBusy[lockKey] = true;

    action().subscribe({
      next: () => delete this.isBusy[lockKey],
      complete: () => delete this.isBusy[lockKey]
    }); //unlock
  }

  public editExpensesBillableFinal(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const lockKey = node.data.entity.id + 'expenseLine';
    this.executeIfLockIsAvailable(lockKey, () => {
      const rowData = node.data;
      const totalExpensesBillable = Number(rowData.totals.totalExpensesBillable);
      const expenseLine = rowData.entity.expenseLine;
      let userInput = Number(rowData.totals.totalExpensesBillableFinal);

      if (userInput === null || isNaN(userInput)) {
        return EMPTY;
      }

      if (userInput === totalExpensesBillable) {
        userInput = undefined;
      }

      if (expenseLine) {
        expenseLine.billableFinal = userInput;
      } else {
        this.estimateService.createExpenseLine(rowData.entity.id, {
          billableFinal: userInput
        });
      }

      return this.saveChangesAndUpdateTotals(node);
    });
  }

  private saveChangesAndUpdateTotals(node: TreeNode<TreeNodeData<EstimateLine>>) {
    return this.estimateService.saveChanges().pipe(
      map(() => {
        this.propagateTotals(node);
        this.totalsData.change();
      })
    );
  }

  public editHoursBillableFinal(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const rowData = node.data;
    const value = Number(rowData.totals.totalHoursBillableFinal);
    const totalHoursCost = Number(rowData.totals.totalHoursBillable);

    if (isNaN(value) || value === totalHoursCost) {
      rowData.entity.laborBillableFinal = null;
    } else {
      rowData.entity.laborBillableFinal = value;
    }

    this.saveChangesAndUpdateTotals(node).subscribe();
  }

  public editInternalFeesBillableFinal(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const rowData = node.data;
    const value = Number(rowData.totals.totalInternalFeesBillableFinal);
    const totalInternalFeesCost = Number(rowData.totals.totalInternalFeesBillable);

    if (isNaN(value) || value === totalInternalFeesCost) {
      rowData.entity.internalFeeBillableFinal = null;
    } else {
      rowData.entity.internalFeeBillableFinal = value;
    }

    this.saveChangesAndUpdateTotals(node).subscribe();
  }

  public clearHoursFinalValue(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const rowData = node.data;
    rowData.entity.laborBillableFinal = null;

    const elem = document.getElementById('hoursBillableFinal') as HTMLInputElement;
    if (elem && elem.value !== null) {
      elem.value = null;
    }

    this.saveChangesAndUpdateTotals(node).subscribe();
  }

  public clearInternalFeesFinalValue(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const rowData = node.data;
    rowData.entity.internalFeeBillableFinal = null;

    const elem = document.getElementById('internalFeesBillableFinal') as HTMLInputElement;
    if (elem && elem.value !== null) {
      elem.value = null;
    }

    this.saveChangesAndUpdateTotals(node).subscribe();
  }

  public clearExpenseFinalValue(node: TreeNode<TreeNodeData<EstimateLine>>) {
    const rowData = node.data;
    rowData.entity.expenseLine.billableFinal = null;

    const elem = document.getElementById('expensesBillableFinal') as HTMLInputElement;
    if (elem && elem.value !== null) {
      elem.value = null;
    }
    this.estimateService.saveChanges().subscribe(() => {
      this.propagateTotals(node);
    });
  }

  private propagateTotals(rowNode: TreeNode<TreeNodeData<EstimateLine>>) {
    // reset totals
    const billingMethods = {
      labor: this.laborBillingMethods,
      internalFees: this.internalFeeBillingMethods,
      expense: this.expenseBillingMethods
    };

    rowNode.data.totals = new TreeNodeTotals(
      this.currentEstimate,
      billingMethods,
      this.averageRates,
      rowNode.data.totals?.departmentCurrency
    );
    // modify treeNodeTotals of rowData
    const isGenericTimeNode = rowNode.data.type === TreeNodeType.GenericTime;
    const isGenericInternalFeeNode = rowNode.data.type === TreeNodeType.GenericInternalFee;
    const isGenericExpenseNode = rowNode.data.type === TreeNodeType.GenericExpense;

    rowNode.data.totals.setTotals(
      this.currentEstimate,
      rowNode.data.entity,
      this.functionalCurrency,
      this.hasPC,
      isGenericTimeNode,
      isGenericInternalFeeNode,
      isGenericExpenseNode
    );

    // modify treeNodeTotals across grid
    let parentNode = rowNode.parent;
    while (parentNode) {
      // reset totals
      parentNode.data.totals = new TreeNodeTotals(
        this.currentEstimate,
        billingMethods,
        this.averageRates,
        rowNode.data.totals?.departmentCurrency
      );
      // sum of all children
      for (const child of parentNode.children) {
        parentNode.data.totals.addToTotals(child.data.totals);
      }
      // next parent
      parentNode = parentNode.parent;
    }
    this.totalsData.changeProjectTotals(this.estimateTree[0].data.totals);
    this.estimateService.saveChanges().subscribe();
  }
}
